import { Vec2 } from 'ayvri-std/lib/vector';
import { cartographicToCartesian, cartesianToCartographic, radiusAtLatitude, dist } from 'ayvri-std/lib/ellipsoid';
import Activity from './classes/activity';

const kWaypointBufferDistance = 100;
const kCourseTrackId = '__courseActivity';

export default class CourseManager {
  constructor() {
    this.course = undefined;
    this.track = undefined;
    this.optimizedPath = undefined;
  }

  onSceneLoad(app, store, scene, dispatch, trackLoader, tracks) {
    const course = scene.course;
    if (!course) return;

    const tracksLoadedPromise = new Promise(resolve => {
      this.tracksLoadedResolve = resolve;
    });

    try {
      switch (course.type) {
        case 'SegmentCourse': {
          const trackChannels = ['time', 'height', 'depth', 'cumulative', 'x', 'y', 'z'];
          this.track = tracks.addTrack(kCourseTrackId, trackChannels);

          // Force the loading bar to wait for the course track to be fully constructed
          dispatch({ type: 'SET_TRACK_READY_TIME', trackId: kCourseTrackId, readyTime: 0.0 });

          // Once all the tracks are loaded, build the Course track from segment points
          tracksLoadedPromise.then(() => {
            const state = store.getState();
            const segPoints = [];

            // Build the track by concatenating points from each segment
            for (let i = 0; i < state.segments.length; i++) {
              const seg = state.segments[i];
              const nextSeg = state.segments[i + 1];
              const segTrack = tracks.getTrack(seg.targetId);

              // Determine start and end indices based on segment start and end time
              const startIdx = Math.floor(segTrack.sampleLinear({ time: seg.time }, { index: -1 }).index);
              const endIdx = nextSeg
                ? Math.ceil(segTrack.sampleLinear({ time: nextSeg.time }, { index: -1 }).index)
                : segTrack.length - 1;
              const count = endIdx - startIdx + 1;

              // Fetch the relevant sections of track for this segment
              segPoints[i] = {};
              trackChannels.forEach(c => {
                segPoints[i][c] = segTrack.fetchChannel(c, startIdx, count);
              });

              // Bias the cumulative channel of each section so that it is continuous
              const cumu = segPoints[i].cumulative;
              const prevDist = i > 0 ? segPoints[i - 1].cumulative[segPoints[i - 1].cumulative.length - 1] : 0;
              segPoints[i].cumulative = cumu.map(distance => distance - cumu[0] + prevDist);
            }

            // Append each segment's points to the track
            segPoints.forEach(p => app.onPointsAdded(kCourseTrackId, { trackVecs: p }));

            // Finish loading
            dispatch({ type: 'SET_TRACK_READY_TIME', trackId: kCourseTrackId, readyTime: Infinity });
          });

          break;
        }

        case 'FixedCourse': {
          // Construct a new activity (track channels need to be computed for point processing)
          this.activity = new Activity(course.activity, 0, 0, scene);

          // Use a different ID to avoid conflicts if the same activity is used in the scene
          this.activity.activityId = kCourseTrackId;

          this.track = tracks.addTrack(this.activity.activityId, this.activity.trackChannels, this.activity.numPoints);

          // Download the course track
          const trackReady = trackLoader.downloadTrack(this.activity, pointData => {
            app.onPointsAdded(kCourseTrackId, pointData);
          });

          // When ready, build an optimizedPath
          trackReady.then(() => {
            this.optimizedPath = this.track;
          });
          break;
        }

        case 'WaypointCourse': {
          // @TODO: Show the active activity's height profile in the course profile (not the waypoint heights)
          this.activityWaypoints = {};

          this.waypoints = course.waypoints.map(w => {
            const cartesian = cartographicToCartesian(w.point.latitude, w.point.longitude, w.point.height);
            return Object.assign({ cartesian }, w);
          });

          // @TODO: Create visual representations of the waypoints

          this.optimizedPath = computeOptimizedPathFromWaypoints(course.waypoints);
          const trackLength = this.optimizedPath.length;

          // Convert points to track format
          const vecs = {
            height: new Float64Array(trackLength),
            cumulative: new Float64Array(trackLength),
            x: new Float64Array(trackLength),
            y: new Float64Array(trackLength),
            z: new Float64Array(trackLength),
          };
          const cartesians = this.optimizedPath.map(p => cartographicToCartesian(p.latitude, p.longitude, p.height));
          for (let i = 0; i < trackLength; i++) {
            const point = this.optimizedPath[i];
            vecs.height[i] = point.height;
            vecs.cumulative[i] = i === 0 ? 0.0 : vecs.cumulative[i - 1] + dist(cartesians[i], cartesians[i - 1]);
            vecs.x[i] = cartesians[i].x;
            vecs.y[i] = cartesians[i].y;
            vecs.z[i] = cartesians[i].z;
          }

          // @TODO: Over-sample the optimized path to form a track
          const channels = ['height', 'cumulative', 'x', 'y', 'z'];
          this.track = tracks.addTrack(kCourseTrackId, channels, trackLength);
          app.onPointsAdded(kCourseTrackId, { trackVecs: vecs });
          break;
        }

        default:
          throw new Error('Invalid Course type');
      }

      // Initialize our state
      dispatch({
        type: 'SET_COURSE',
        course: {
          desc: course,
          path: this.optimizedPath,
        },
      });

      this.course = course;
    } catch (e) {
      console.error('Failed to load Course:', e);
    }
  }

  onPlayerReady() {
    // @DEBUG: Draw the optimized path using Cesium polylines. We may want to enable this as a real
    //         feature at some point.
    // if (this.course && kDrawOptimizedPath) {
    //   const cartesians = this.optimizedPath.map(p => cartographicToCartesian(p.latitude, p.longitude, p.height));
    //   globe.dataSourceDisplay.defaultDataSource.entities.add({
    //     polyline: {
    //       positions: cartesians,
    //       depthFailMaterial: Cesium.Color.WHITE,
    //       width: 2.0,
    //     },
    //   });
    // }
  }

  update(store) {
    const state = store.getState();

    // Resolve once all non-course tracks have fully loaded
    if (this.tracksLoadedResolve) {
      const trackLoading = Object.keys(state.loading.tracks).map(k => state.loading.tracks[k]);
      if (trackLoading.reduce((acc, cur) => acc && cur.complete, true)) {
        this.tracksLoadedResolve();
        delete this.tracksLoadedResolve;
      }
    }
  }

  sampleDistance(activityId, cartesian, cumulative) {
    const result = { cumulative: 0.0 };
    if (!this.course || !this.track) return result.cumulative;

    switch (this.course.type) {
      case 'WaypointCourse': {
        const currentWaypointIdx = this.activityWaypoints[activityId] || 0;
        const currentWaypoint = this.waypoints[currentWaypointIdx];

        // If we've hit the last waypoint, we're done
        if (currentWaypointIdx >= this.waypoints.length) return this.track.cumulative.last;

        const earthRad = radiusAtLatitude(currentWaypoint.point.latitude);
        const carto = cartesianToCartographic(cartesian);
        const distToTarget = cartographicDist(carto, currentWaypoint.point, earthRad) - currentWaypoint.radius;

        // This activity has tagged the current waypoint, target the next one
        if (distToTarget < kWaypointBufferDistance) {
          this.activityWaypoints[activityId] = currentWaypointIdx + 1;
        }

        // Compute the remaining distance left in the course
        const remainingPath = this.optimizedPath.slice(this.activityWaypoints[activityId]);
        const remainingDists = remainingPath.map((w, i) =>
          cartographicDist(w, i === 0 ? carto : remainingPath[i - 1], earthRad),
        );
        const remainingDist = remainingDists.reduce((acc, cur) => acc + cur, 0);

        return this.track.cumulative.last - remainingDist;
      }

      case 'FixedCourse': {
        // @TODO: Compute offset from start and bias
        return cumulative;
      }

      default:
        this.track.findNearestPoint(cartesian, result);
        return result.cumulative;
    }
  }
}

// @NOTE: This method works for ground and aerial activities. It does not take height into account
// @TODO: Take a starting point so that we can use this to compute remaining distance, and hence %
// @TODO: Support exit cylinders
// @TODO: Needs to support starting outside of the first waypoint (!!!)
function computeOptimizedPathFromWaypoints(waypoints) {
  console.assert(waypoints.length >= 2);
  const scatchVec2 = new Vec2();
  const res = [];
  const earthRad = radiusAtLatitude(waypoints[0].point.latitude);

  // Convert to Vec2s
  const wps = waypoints.map(w => new Vec2(w.point.latitude, w.point.longitude));

  // Convert the waypoint radii into angular radii
  const radii = waypoints.map(w => w.radius / radiusAtLatitude(w.point.latitude));

  // See https://www.movable-type.co.uk/scripts/latlong.html
  function getBearing(a, b) {
    const dlon = b.y - a.y;
    const cosLat = Math.cos(b.x);
    const lon = Math.sin(dlon) * cosLat;
    const lat = Math.cos(a.x) * Math.sin(b.x) - Math.sin(a.x) * cosLat * Math.cos(dlon);
    return new Vec2(lon, lat);
  }

  function toRadian(bearing) {
    return Math.atan2(bearing.x, bearing.y);
  }

  function findNextDifferentCenter(idx) {
    const base = wps[idx];
    let minRad = radii[idx];
    for (let i = idx + 1; i < wps.length; i++) {
      const centerDiff = Vec2.subtract(wps[i], base, scatchVec2).length();

      // When skipping concentric waypoints, we must still hit the smallest radius
      if (centerDiff > 1.0e-7) return { p: wps[i], r: minRad };
      else minRad = Math.min(minRad, radii[i]);
    }
    return { p: undefined, r: minRad };
  }

  function distance(p0, p1) {
    const R = radiusAtLatitude(p0.x);
    const φ1 = p0.x;
    const φ2 = p1.x;
    const Δφ = φ2 - φ1;
    const Δλ = p1.y - p0.y;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  function crossTrackDistance(startPoint, endPoint, p) {
    const R = radiusAtLatitude(p.x);
    const δ13 = distance(startPoint, p) / R;
    const θ13 = toRadian(getBearing(startPoint, p));
    const θ12 = toRadian(getBearing(startPoint, endPoint));
    return Math.asin(Math.sin(δ13) * Math.sin(θ13 - θ12)) * R;
  }

  function alongTrackDistance(startPoint, dXt, p) {
    const R = radiusAtLatitude(p.x);
    const δ13 = distance(startPoint, p) / R;
    return Math.acos(Math.cos(δ13) / Math.cos(dXt / R)) * R;
  }

  function translate(pos, bearing, angDist) {
    const lat = Math.asin(
      Math.sin(pos.x) * Math.cos(angDist) + Math.cos(pos.x) * Math.sin(angDist) * Math.cos(bearing),
    );
    const lon =
      pos.y +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angDist) * Math.cos(pos.x),
        Math.cos(angDist) - Math.sin(pos.x) * Math.sin(lat),
      );
    return new Vec2(lat, lon);
  }

  // Find the first point that isn't the same as the start, in the case of start radiuses of increasing size.
  // Exclusively varying radiuses on the same point is a valid course, so if there isn't one, pick a random direction
  const { p: firstTarget, r: firstRad } = findNextDifferentCenter(0);
  const startBearing = firstTarget ? toRadian(getBearing(wps[0], firstTarget)) : 0;
  res[0] = translate(wps[0], startBearing, firstRad);

  // Compute middle waypoints based on bisection of successive vertices
  for (let i = 1; i < wps.length - 1; i++) {
    // In case consecutive points have the same center, find the next distinct one
    // While recording the minimum radius that we must hit for the concentric points
    const { p: nextPoint, r: minRad } = findNextDifferentCenter(i, minRad);

    // If the previous result puts us inside this waypoint, reuse the result
    const wpDist = distance(res[i - 1], wps[i]);
    if (wpDist <= waypoints[i].radius + 1) {
      res[i] = res[i - 1];
    } else {
      const vb = getBearing(wps[i], res[i - 1]).normalize();

      let vh;
      if (!nextPoint) {
        // If all future points have the same center, aim straight for it.
        res[i] = translate(wps[i], toRadian(vb), minRad);
      } else {
        // The optimal point lies on the bisection of the vectors to the adjacent points
        const vf = getBearing(wps[i], nextPoint).normalize();
        vh = Vec2.lerp(vb, vf, 0.5).normalize();

        // If the next and previous bearings are completely opposite, go right through the center
        if (Number.isNaN(vh.x) || Number.isNaN(vh.y)) {
          res[i] = wps[i];
        } else {
          let pushDist = minRad;

          // If the distance to intersect the line between next and prev points is less than the radius,
          // i.e. the path to the next waypoint takes us through this one, use that distance instead
          const dxt = crossTrackDistance(res[i - 1], nextPoint, wps[i]);
          const dxtAng = Math.abs(dxt) / earthRad;
          if (dxtAng < minRad) {
            const dat = alongTrackDistance(res[i - 1], dxt, wps[i]);
            if (dat < distance(res[i - 1], nextPoint)) pushDist = dxtAng;
          }

          res[i] = translate(wps[i], toRadian(vh), pushDist);
        }
      }
    }
  }

  // Last point
  const lastIdx = wps.length - 1;
  const endBearing = toRadian(getBearing(wps[lastIdx], res[wps.length - 2]));
  res[lastIdx] = translate(wps[lastIdx], endBearing, radii[lastIdx]);

  // Remove duplicates
  // @NOTE: I think we want to maintain a 1:1 mapping of waypoint to optimized point, for distance-along-course calculation
  // const result = [res[0]];
  // for (let i = 1; i < res.length; i++) {
  //   if (res[i] !== res[i - 1]) result.push(res[i]);
  // }

  // Re-project back into cartographic points
  return res.map((r, i) => ({
    latitude: r.x,
    longitude: r.y,
    height: waypoints[i].point.height,
    cartesian: cartographicToCartesian(r.x, r.y, waypoints[i].point.height),
  }));
}

function cartographicDist(p0, p1, earthRad) {
  const R = earthRad;
  const φ1 = p0.latitude;
  const φ2 = p1.latitude;
  const Δφ = φ2 - φ1;
  const Δλ = p1.longitude - p0.longitude;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export function courseReducer(state = null, action) {
  switch (action.type) {
    case 'SET_COURSE':
      return action.course;
    default:
      return state;
  }
}
